import { DEEP_LINK_COMPATIBILITY_MONTHS, DEEP_LINK_VERSION } from './constants';
import { Rating, RatingIntent } from './types';

/**
 * The ticket deep-link contract (Requirement 8).
 *
 * Pure and clock-driven: every outcome is a function of the six parameters and the supplied `now`.
 * Nothing here reads, writes, or emits — the page combines this result with the session-read outcome
 * to reach `analysis-not-found`, and the page is what puts the `cause` on a metric.
 */

// ─── Parameter and outcome shapes ────────────────────────────────────────────

/** Path segments first, then query parameters, in the order a link presents them. */
export const DEEP_LINK_PARAM_NAMES = [
  'clientId',
  'domainName',
  'ticketId',
  'v',
  'r',
  'sessionId',
] as const;

export type DeepLinkParamName = typeof DEEP_LINK_PARAM_NAMES[number];

/** Raw input as the router and query string supply it — any parameter may be absent. */
export type RawDeepLinkParams = Partial<
  Record<DeepLinkParamName, string | null | undefined>
>;

/**
 * A closed set, because the page puts this value on a metric dimension (Requirement 18 criterion 12b)
 * and an open set would make the dimension unbounded.
 *
 * **`sessionId-mismatch` is gone, along with the derivation that produced it.** The session the analysis
 * is written under embeds whatever ticket identifier the `ticket_interceptor` received, and EventBridge
 * delivers the *extended* (UUID) form — observed live as
 * `287773672611_test-summary-7_0b820c7c-1419-47b0-bd89-22d659f2463e`. The link's path segment carries the
 * short id, so no derivation from the path can reproduce the real session name. Comparing against one
 * rejected every genuine link as invalid, which is a worse failure than the spoofing it guarded against:
 * it blamed the link for our own arithmetic.
 */
export type InvalidLinkCause =
  | 'clientId-format'
  | 'domainName-format'
  | 'ticketId-format'
  | 'r-value'
  | 'sessionId-pattern';

export interface ResolvedDeepLink {
  clientId: string;
  domainName: string;
  ticketId: string;
  version: string;
  ratingIntent: RatingIntent;
  /**
   * The **carried** session id, verbatim from the link.
   *
   * Not derived. Only the interceptor knows which identifier form went into the session name, so the
   * value it wrote is the only correct one. Validated against the AgentCore pattern, never recomputed.
   */
  sessionId: string;
  /** `down` pre-sets `Down`, `up` pre-sets `Up`. This is the only thing `r` decides. */
  presetRating: Rating;
}

/**
 * Four of the five outcomes of Requirement 8. The fifth, `analysis-not-found`, depends on the
 * session read and so is the page's to reach — keeping it out of here is what keeps this function
 * pure.
 */
export type DeepLinkResult =
  | {
      outcome: 'named-missing-parameter';
      missing: DeepLinkParamName;
      allMissing: DeepLinkParamName[];
    }
  | { outcome: 'invalid-link'; cause: InvalidLinkCause }
  | { outcome: 'expired-link'; version: string; sunsetOn: Date }
  | { outcome: 'renderable'; link: ResolvedDeepLink };

// ─── Formats ─────────────────────────────────────────────────────────────────

const CLIENT_ID_PATTERN = /^\d{12}$/;
const DOMAIN_NAME_PATTERN = /^[a-zA-Z0-9-]+$/;
/**
 * Deliberately as permissive as the server's `TICKET_ID_PATTERN`, and it must stay that way.
 *
 * This was two narrower patterns, `/^[A-Z]\d{6,12}$/` and `/^[A-Z][A-Z0-9]*-\d+$/`, both requiring a leading
 * uppercase letter. A UUID-form ticket id matches neither — and a UUID is a form SIM-T actually issues, as
 * the server's own comment on `TICKET_ID_PATTERN` records. So an analysis stored under one could be written
 * but never linked: the parser answered `invalid-link` for a link the interceptor had legitimately produced.
 *
 * Narrowness bought nothing here. What makes a ticket id safe as a sort-key segment is the exclusion of the
 * delimiters — `#`, `|` and whitespace — which this pattern already does, and which is the sole reason the
 * server's is shaped the way it is. Requiring a leading capital letter guards nothing; it only decides which
 * real ticket ids get feedback links.
 *
 * Kept in step with `server/utils/merlinProvenance.ts` by hand, because the client build only includes `src`.
 * That duplication is exactly how the two drifted apart, so widen both together or neither: a value this
 * accepts and the server rejects is an unopenable link, and the reverse is an unlinkable analysis.
 */
const TICKET_ID_PATTERN = /^[a-zA-Z0-9-]{1,64}$/;
/** The AgentCore session-id constraint: 100 chars, leading alphanumeric, no colons. */
const SESSION_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9\-_]{0,99}$/;

const RATING_INTENTS: readonly RatingIntent[] = ['up', 'down'];

/**
 * The link's lowercase `r` mapped to the stored Vote value.
 *
 * Kept as a map even though the two now differ only in case: `r` is a URL contract already emitted by the
 * ticket interceptor into links that exist in tickets today, while the Vote is what the store holds. A
 * capitalisation helper would tie the two together and make either one impossible to change alone.
 */
const PRESET_RATING: Readonly<Record<RatingIntent, Rating>> = {
  down: 'Down',
  up: 'Up',
};

// ─── Version table ───────────────────────────────────────────────────────────

interface VersionEntry {
  /**
   * When a superseding version was published, or `null` while this version is current.
   *
   * The sunset is derived from this rather than stored, because the requirement defines the
   * compatibility period as running *from the publication of the superseding version*. Retiring a
   * version is therefore a one-line change — set this date — and no parser is deleted.
   */
  supersededOn: Date | null;
}

const VERSIONS: Readonly<Record<string, VersionEntry>> = {
  [DEEP_LINK_VERSION]: { supersededOn: null },
};

function sunsetOf(entry: VersionEntry): Date | null {
  if (entry.supersededOn === null) return null;
  const sunset = new Date(entry.supersededOn.getTime());
  sunset.setMonth(sunset.getMonth() + DEEP_LINK_COMPATIBILITY_MONTHS);
  return sunset;
}

// ─── Session id ──────────────────────────────────────────────────────────────

/**
 * There is deliberately **no `deriveSessionId` here.**
 *
 * It used to mirror `AgentCoreClient._build_session_id` so the page could recompute the session name and
 * reject a link whose carried value disagreed. That mirror cannot work: `_build_session_id` interpolates
 * the ticket identifier it was *given*, and on a real EventBridge event that is the extended UUID form,
 * while the link's path carries the short id. The two can never agree, so the comparison rejected every
 * genuine link.
 *
 * The consequence to accept: the carried `sessionId` is no longer corroborated, so a crafted link could
 * name another ticket's analysis session. It is still pattern-validated, the read it feeds is scoped to
 * the fixed `ticket_interceptor` actor, and it reaches only an authenticated caller — but the structural
 * guarantee is gone, and nothing can restore it while the two identifier forms differ.
 */

// ─── Parsing ─────────────────────────────────────────────────────────────────

/**
 * `''` counts as absent, which is a judgement call worth stating.
 *
 * A URL carrying `?r=` has the parameter present but empty. Naming it as missing is more useful than
 * reporting it as malformed: the remedy is the same — supply a value — and "missing `r`" points at the
 * link's shape while "invalid `r`" would send the reader looking at a value that isn't there.
 */
function isAbsent(value: string | null | undefined): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Resolve a deep link to exactly one outcome.
 *
 * Precedence, and it is the requirement's order rather than a convenient one:
 *
 * 1. **Absence** beats everything. A missing parameter is named even when other present parameters
 *    are malformed — the user needs to know what is missing, and a malformation message about a
 *    different parameter would send them looking in the wrong place.
 * 2. **Malformation**, with path segments treated identically to query parameters. There is no reason
 *    for a caller to care which half of the URL a bad value came from.
 * 3. **Version sunset** last. So a well-formed but expired link reports expiry, while a malformed
 *    expired link reports the malformation — the actionable fault first.
 */
export function parseDeepLink(
  params: RawDeepLinkParams,
  now: Date
): DeepLinkResult {
  const allMissing = DEEP_LINK_PARAM_NAMES.filter((name) =>
    isAbsent(params[name])
  );
  if (allMissing.length > 0) {
    // Named deterministically in contract order, so the same broken link always reports the same
    // parameter rather than one that depends on object iteration.
    return {
      outcome: 'named-missing-parameter',
      missing: allMissing[0],
      allMissing,
    };
  }

  const clientId = String(params.clientId);
  const domainName = String(params.domainName);
  const ticketId = String(params.ticketId);
  const version = String(params.v);
  const rawIntent = String(params.r);
  const carriedSessionId = String(params.sessionId);

  if (!CLIENT_ID_PATTERN.test(clientId))
    return { outcome: 'invalid-link', cause: 'clientId-format' };
  if (!DOMAIN_NAME_PATTERN.test(domainName))
    return { outcome: 'invalid-link', cause: 'domainName-format' };
  if (!TICKET_ID_PATTERN.test(ticketId))
    return { outcome: 'invalid-link', cause: 'ticketId-format' };

  // Case-sensitive on purpose: `Up` and `DOWN` are invalid. A case-insensitive comparison would
  // accept links this contract never produced.
  if (!RATING_INTENTS.includes(rawIntent as RatingIntent))
    return { outcome: 'invalid-link', cause: 'r-value' };
  const ratingIntent = rawIntent as RatingIntent;

  // The carried value is validated, not recomputed: a value AgentCore itself would refuse cannot name a
  // session, whoever produced it.
  if (!SESSION_ID_PATTERN.test(carriedSessionId))
    return { outcome: 'invalid-link', cause: 'sessionId-pattern' };

  /*
   * An own-property check, not a plain index.
   *
   * `version` is the one link parameter with no format pattern, and `VERSIONS` is an object literal, so a
   * plain `VERSIONS[version]` also resolves inherited members: `?v=toString` returns a function, `?v=__proto__`
   * the prototype. Either is `!== undefined`, so it slips past the guard below and reaches `sunsetOf`, where
   * `entry.supersededOn === null` is false because it is `undefined` — and `.getTime()` on that throws.
   *
   * The visible effect is that a hand-edited `v` turns the expired-link page into a TypeError. Guarded here
   * rather than by adding a pattern for `version`, because the table is the actual authority on which
   * versions exist and a pattern would still admit any well-formed unknown value.
   */
  const entry = Object.prototype.hasOwnProperty.call(VERSIONS, version)
    ? VERSIONS[version]
    : undefined;
  if (entry === undefined) {
    // An unknown version is reported as expiry rather than malformation: from the reader's side the
    // remedy is the same — obtain a current link — and a version this build has never heard of is
    // more likely one that has been retired than one that is mistyped.
    return { outcome: 'expired-link', version, sunsetOn: new Date(0) };
  }
  const sunsetOn = sunsetOf(entry);
  if (sunsetOn !== null && now.getTime() >= sunsetOn.getTime()) {
    return { outcome: 'expired-link', version, sunsetOn };
  }

  return {
    outcome: 'renderable',
    link: {
      clientId,
      domainName,
      ticketId,
      version,
      ratingIntent,
      sessionId: carriedSessionId,
      presetRating: PRESET_RATING[ratingIntent],
    },
  };
}

// ─── Building ────────────────────────────────────────────────────────────────

export interface DeepLinkInputs {
  clientId: string;
  domainName: string;
  ticketId: string;
  /**
   * The session the analysis was written under, as the producer computed it.
   *
   * Required, and not defaulted: only the caller knows which identifier form went into it, and a default
   * would silently reintroduce the derivation this contract removed.
   */
  sessionId: string;
}

/** Build one link from values the caller already holds. */
export function buildDeepLink(
  inputs: DeepLinkInputs,
  ratingIntent: RatingIntent
): string {
  const { clientId, domainName, ticketId, sessionId } = inputs;
  /**
   * Enforced at runtime, not only in the type.
   *
   * `URLSearchParams` coerces `undefined` to the literal string `"undefined"`, which then *passes*
   * `SESSION_ID_PATTERN` on the way back in — so `parseDeepLink` would return `renderable` carrying a
   * session id that names nothing, and the read would look for an analysis under it. A silent wrong-session
   * read is far worse than a thrown error at the point of construction. Tests are not type-checked
   * (`tsconfig` has `include: ["src"]`), so the compiler cannot be the guard here.
   */
  if (!sessionId) {
    throw new Error('buildDeepLink requires a sessionId');
  }
  /*
   * A second, separate check — not a replacement for the one above, which must stay.
   *
   * `SESSION_ID_PATTERN.test(undefined)` coerces its argument, so the string `"undefined"` matches and the
   * pattern alone would wave through exactly the value the truthiness check exists to stop. The two catch
   * different faults: absence before `URLSearchParams` can coerce it, and structural invalidity after.
   *
   * Without this one, a session id the parser forbids — a colon, or a leading `_` — still builds a link, and
   * `parseDeepLink` then answers `invalid-link` with cause `sessionId-pattern`. That is the same
   * builder/parser drift the three segment checks below exist to prevent, on the one field they skipped.
   */
  if (!SESSION_ID_PATTERN.test(sessionId)) {
    throw new Error(
      'buildDeepLink requires a sessionId the feedback page can parse'
    );
  }
  /**
   * The path segments are checked against the same patterns `parseDeepLink` will apply to them.
   *
   * Without this the builder emits links the parser refuses — a URL that looks correct in a ticket and is
   * permanently unopenable, which nobody discovers until someone clicks it.
   *
   * These checks are only sound while each pattern is no narrower than what the producer can legitimately
   * hand over. A throw here is strictly worse than a dead link if it fires on a real ticket id: the
   * interceptor builds both the up and down link for an analysis, so one rejected id means that analysis gets
   * no feedback links at all, and silently. That is why `TICKET_ID_PATTERN` matches the server's storable set
   * rather than a guess at ticket-id shape — see the note on it above.
   *
   * Thrown at construction for the reason given above for `sessionId`: a fault the builder can see now is
   * worth more than one discovered by a reader.
   */
  if (!CLIENT_ID_PATTERN.test(clientId)) {
    throw new Error('buildDeepLink requires a 12-digit clientId');
  }
  if (!DOMAIN_NAME_PATTERN.test(domainName)) {
    throw new Error('buildDeepLink requires an alphanumeric domainName');
  }
  if (!TICKET_ID_PATTERN.test(ticketId)) {
    throw new Error(
      'buildDeepLink requires a ticketId the feedback page can parse'
    );
  }
  const query = new URLSearchParams({
    v: DEEP_LINK_VERSION,
    r: ratingIntent,
    sessionId,
  });
  return `/${clientId}/${domainName}/feedback/${ticketId}?${query.toString()}`;
}

/**
 * Both links for one analysis, which is what the ticket_interceptor writes back.
 *
 * Exported as a pair so the two-link contract has one source of truth and is executable rather than
 * only prose — the interceptor emits two links where it emitted one, and this is the definition of
 * what those two are.
 */
export function buildDeepLinks(inputs: DeepLinkInputs): {
  down: string;
  up: string;
} {
  return {
    down: buildDeepLink(inputs, 'down'),
    up: buildDeepLink(inputs, 'up'),
  };
}
